import { ActivatedRoute, Router } from '@angular/router';
import { Component, Input, OnInit, QueryList, ViewChildren } from '@angular/core';

import { UrlSegments, ComponentRoutes } from '../jurisdiction.constants';
import { CommonService } from '../../services/common.service';
import { JurisdictionService } from '../jurisdiction.service';
import FacilityServiceStrategy from './services/facility-service.strategy';
import { ErrorService } from '../../services/error/error.service';
import { EnvironmentService } from '../../services/environment.service';
import { DestroyableComponent } from '../../common/destroyable.component';
import { FormTypesData, KeyValueData } from '../../domains/common.domain';
import { LocalStorageService } from '../../services/local-storage.service';
import * as FacilitiesFormDomain from '../facilities-form/facilities-form.domain';
import { FacilitiesFormService } from '../facilities-form/facilities-form.service';
import { FacilitiesFormComponent } from '../facilities-form/facilities-form.component';
import { FacilityFormTypes, InstallationFlowType } from '../../constants/app.constants';
import { NavigationStateShareService } from '../../services/navigation-state-share.service';
import { CustomFieldsService } from 'src/app/+shared/custom-fields/custom-fields.service';
import { ComponentCanDeactivate } from 'src/app/+shared/guards/pending-changes-guard.servie';
import { AbstractControl } from '@angular/forms';

@Component({
  selector: 'app-facilities-data',
  templateUrl: './facilities-data.component.html',
  styleUrls: ['./facilities-data.component.scss'],
})
export class FacilitiesDataComponent extends DestroyableComponent implements OnInit, ComponentCanDeactivate {
  @ViewChildren(FacilitiesFormComponent)
  facilitiesFormDataChildren: QueryList<FacilitiesFormComponent>;

  @Input() currentFormType: FormTypesData;
  @Input() isReadOnly: boolean = false;

  showData: boolean = true;
  technicalDataArray: FacilitiesFormDomain.TechnicalData[];
  isLoading: boolean = false;

  currentFlowType: string;
  installationFlowType: KeyValueData;
  isMeterInstallation: boolean = true;
  facilityTypes: FormTypesData[];
  showNewMeterSection: boolean;
  showOldMeterSection: boolean;
  systemId: string;

  gasInstallationId: string;
  heatingInstallationId: string;
  powerInstallationId: string;
  waterInstallationId: string;
  isDetailsView: boolean;
  preventCanDeactivateCheck: boolean = false;
  meterFG: AbstractControl;
  applicationId: number;

  constructor(
    private route: Router,
    private errorService: ErrorService,
    private commonService: CommonService,
    private environmentService: EnvironmentService,
    private stateShare: NavigationStateShareService,
    private localStorageService: LocalStorageService,
    private jurisdictionService: JurisdictionService,
    private facilitiesFormService: FacilitiesFormService,
    private facilityService: FacilityServiceStrategy,
    private activatedRoute: ActivatedRoute,
    private customFieldsService: CustomFieldsService
  ) {
    super();
  }

  canDeactivate(): boolean {
    return this.preventCanDeactivateCheck;
  }

  ngOnInit(): void {
    this.preventCanDeactivateCheck = false;
    this.preventLeak(this.activatedRoute.url).subscribe(urlSegments => {
      this.isDetailsView = urlSegments[0].path === ComponentRoutes.ProcessDetails;
    });
    this.setFacilityTypes();
    this.setCurrentFormType();
    this.setInstallationData();
    this.initTechnicalData();
    this.saveInstallationData();

    this.customFieldsService.loadCustomFieldConfigForApplication(this.currentFlowType);

    this.facilitiesFormService.registerForComponentFormUpdate.subscribe(meterData => {
      if (meterData.component !== 'oldMeterData') return;

      this.meterFG = meterData.formData;
    });

    const applicationId: string = this.localStorageService.getItem('application_id');
    this.applicationId = parseInt(applicationId);
  }

  disableSubmitBtn(): boolean {
    return (this.meterFG && !this.meterFG.valid) || this.facilitiesFormDataChildren.some(x => !x.isValidForm());
  }

  submitForm(): Promise<void> {
    if (!this.showData) {
      this.showData = true;
    }
    return this.updateInstallationData();
  }

  onCloneTechnicalData(data: any) {
    this.technicalDataArray.push(data);
  }

  onDeleteTechnicalData(index: number) {
    this.technicalDataArray.splice(index, 1);
  }

  private initTechnicalData() {
    this.technicalDataArray = [this.getEmptyTechnicalData()];
  }

  private getEmptyTechnicalData(): FacilitiesFormDomain.TechnicalData {
    return {
      baseData: [] as FacilitiesFormDomain.BaseDataObject[],
      meterData: [] as FacilitiesFormDomain.MeterDataObject[],
      newFacilityData: {} as FacilitiesFormDomain.FacilityDataObject,
      oldMeterData: [] as FacilitiesFormDomain.MeterDataObject[],
      construction: {},
      exhaustSystem: {},
      fireplaceVentilation: {},
      releases: {},
      meterInstallation: {},
      furnaceData: {} as FacilitiesFormDomain.FurnaceDataObject,
      firingSystemData: {} as FacilitiesFormDomain.FiringSystemDataObject,
      chimneySweepData: {},
    };
  }

  private setFacilityTypes() {
    this.facilityTypes = this.localStorageService.getItem('selected_facilities');
  }

  private setCurrentFormType(): void {
    if (!this.isDetailsView && !this.isReadOnly) {
      this.currentFormType = this.jurisdictionService.setCurrentFormType(this.currentFormType, this.facilityTypes);
    }
  }

  private setInstallationData() {
    this.currentFlowType = this.localStorageService.getItem('installation_flow');
    this.showNewMeterSection =
      this.environmentService.showNewMeterSection ||
      (!this.environmentService.showNewMeterSection && this.currentFlowType !== InstallationFlowType.UpdateInstallation);
    this.showOldMeterSection =
      this.environmentService.showOldMeterSection && this.currentFlowType === InstallationFlowType.UpdateInstallation;

    this.gasInstallationId = this.localStorageService.getItem('gas_installation_id');
    this.heatingInstallationId = this.localStorageService.getItem('heating_installation_id');
    this.powerInstallationId = this.localStorageService.getItem('power_installation_id');
    this.waterInstallationId = this.localStorageService.getItem('water_installation_id');
  }

  private saveInstallationData(): void {
    if (this[`${this.currentFormType.key}InstallationId`]) {
      this.getInstallationApplication();
      return;
    }

    const applicationId: string = this.localStorageService.getItem('application_id');
    const jurisdictionData = this.localStorageService.getItem('location_data');
    let requestUrlSegment: string;
    const requestData: FacilitiesFormDomain.InstallationData = {
      application_id: applicationId,
    };
    this.isLoading = true;

    switch (this.currentFormType.key) {
      case FacilityFormTypes.Power.key:
        requestUrlSegment = UrlSegments.CreatePowerInstallation;
        requestData.sk_inst_change_type = jurisdictionData.powerType;
        break;
      case FacilityFormTypes.Gas.key:
        requestUrlSegment = UrlSegments.CreateGasInstallation;
        requestData.sk_inst_change_type_gas = jurisdictionData.gasType;
        break;
      case FacilityFormTypes.Water.key:
        requestUrlSegment = UrlSegments.CreateWaterInstallation;
        requestData.sk_inst_change_type_water = jurisdictionData.waterType;
        break;
      case FacilityFormTypes.Heating.key:
        requestUrlSegment = UrlSegments.CreateHeatingInstallation;
        requestData.sk_inst_change_type_districtheat = jurisdictionData.heatingType;
        break;
    }

    this.preventLeak(this.facilitiesFormService.saveData(requestUrlSegment, requestData)).subscribe(
      response => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        this.getInstallationApplication();

        switch (this.currentFormType.key) {
          case FacilityFormTypes.Power.key:
            this.powerInstallationId = (response as FacilitiesFormDomain.PowerInstallationResponse).powerinstallation[0].installation_id;
            this.localStorageService.setItem(`power_installation_id`, this.powerInstallationId);
            break;
          case FacilityFormTypes.Gas.key:
            this.gasInstallationId = (response as FacilitiesFormDomain.GasInstallationResponse).gasinstallation[0].installation_id;
            this.localStorageService.setItem(`gas_installation_id`, this.gasInstallationId);
            break;
          case FacilityFormTypes.Water.key:
            this.waterInstallationId = (response as FacilitiesFormDomain.WaterInstallationResponse).waterinstallation[0].installation_id;
            this.localStorageService.setItem(`water_installation_id`, this.waterInstallationId);
            break;
          case FacilityFormTypes.Heating.key:
            this.heatingInstallationId = (
              response as FacilitiesFormDomain.HeatingInstallationResponse
            ).districtheatinstallation[0].installation_id;
            this.localStorageService.setItem(`heating_installation_id`, this.heatingInstallationId);
            break;
        }
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private getInstallationApplication(): void {
    this.isLoading = true;

    this.preventLeak(this.facilitiesFormService.getInstallationApplication()).subscribe(
      (response: FacilitiesFormDomain.GetInstallationApplicationObject) => {
        this.errorService.throwErrorIfFailure(response, true);
        this.commonService.installationApplicationData = response.installationapplication[0];
        this.parseData();
        this.isLoading = false;
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private parseData(): void {
    const installationData: FacilitiesFormDomain.InstallationApplicationData = { ...this.commonService.installationApplicationData };
    this.facilitiesFormService.installationApplicationData = installationData;
    this.facilitiesFormService.parseApplicationDataIntoForm(this.facilityTypes);
    this.prepareFormData();
  }

  private prepareFormData(): void {
    const data = this.facilitiesFormService.getFormData(this.currentFormType.key);
    if (this.environmentService.copyExistingTechnicalDataConfiguration) {
      if (data?.meterData?.length) {
        this.technicalDataArray = data.meterData.map((meterData, index) =>
          this.buildTechnicalData(data, this.technicalDataArray[index], [data.baseData[index]], [meterData], [data.oldMeterData[index]])
        );
      }
    } else {
      this.technicalDataArray = [
        this.buildTechnicalData(
          data,
          this.technicalDataArray.find(() => true),
          data.baseData || [],
          data.meterData || [],
          data.oldMeterData || []
        ),
      ];
    }
  }

  private buildTechnicalData(
    data: any,
    currentData: FacilitiesFormDomain.TechnicalData,
    baseData: FacilitiesFormDomain.BaseDataObject[],
    meterData: FacilitiesFormDomain.MeterDataObject[],
    oldMeterData: FacilitiesFormDomain.MeterDataObject[]
  ) {
    currentData = currentData || this.getEmptyTechnicalData();
    if (!data) {
      return currentData;
    }

    return {
      ...currentData,
      baseData,
      meterData,
      oldMeterData,
      newFacilityData: data.newFacility ||
        data.newFacilityData || {
          installationId: '',
          measuringPointOperator: '',
          company: '',
        },
      firingSystemData: data.firingData,
      furnaceData: data.furnaceData,
      chimneySweepData: data?.chimneySweepData || {},
    };
  }

  private async updateInstallationData(): Promise<void> {
    for (let i = 0; i < this.technicalDataArray.length; i++) {
      await new Promise<void>((resolve, reject) => {
        const technicalData = this.technicalDataArray[i];

        const installationData = technicalData.newFacilityData;

        const requestData: FacilitiesFormDomain.InstallationData = {
          measuring_point_company: installationData?.company,
          ...(this.isMeterInstallation && {
            sk_measuring_point_operator: installationData?.measuringPointOperator || technicalData.newFacilityData.measuringPointOperator,
          }),
        };

        if (this.currentFormType.index === 1 && technicalData.chimneySweepData?.sk_chim_sweep_confirm) {
          requestData.sk_chim_sweep_confirm = technicalData.chimneySweepData.sk_chim_sweep_confirm;
        }
        this.isLoading = true;

        const { installationId, hasMeter, hasAdditionalData, hasOldMeter } = this.getSaveInstallationParams();

        this.preventLeak(
          this.facilityService.saveInstallation(
            this.currentFormType.key,
            installationId,
            requestData,
            hasMeter,
            hasAdditionalData,
            hasOldMeter,
            technicalData
          )
        ).subscribe(
          response => {
            this.errorService.rejectIfError(response, reject);
            this.errorService.throwErrorIfFailure(response, true);
            resolve();
            this.postSuccessfulSave();
            this.isLoading = false;
          },

          error => {
            reject(error);
            this.isLoading = false;
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      });
    }
  }

  private getSaveInstallationParams(): {
    installationId: string;
    hasMeter: boolean;
    hasAdditionalData?: boolean;
    hasOldMeter?: boolean;
  } {
    if (this.currentFormType.key === FacilityFormTypes.Power.key) {
      return {
        installationId: this.powerInstallationId,
        hasMeter: this.showNewMeterSection,
        hasOldMeter: this.showOldMeterSection,
      };
    }

    if (this.currentFormType.key === FacilityFormTypes.Gas.key) {
      return {
        installationId: this.gasInstallationId,
        hasMeter: this.showNewMeterSection && this.isMeterInstallation,
        hasOldMeter: this.showOldMeterSection,
        hasAdditionalData:
          (this.currentFlowType === InstallationFlowType.NewInstallation ||
            this.currentFlowType === InstallationFlowType.UpdateInstallation) &&
          this.environmentService?.showTechnicalDataFeverPlantSection,
      };
    }

    if (this.currentFormType.key === FacilityFormTypes.Water.key) {
      return {
        installationId: this.waterInstallationId,
        hasMeter: this.showNewMeterSection,
        hasOldMeter: this.showOldMeterSection,
      };
    }

    return {
      installationId: this.heatingInstallationId,
      hasMeter: this.showNewMeterSection,
      hasOldMeter: this.showOldMeterSection,
    };
  }

  private postSuccessfulSave(): void {
    if (!this.isDetailsView) {
      if (this.currentFormType.index < this.facilityTypes[this.facilityTypes.length - 1].index) {
        setTimeout(() => {
          this.setCurrentFormType();
          this.saveInstallationData();
          this.initTechnicalData();
          this.facilitiesFormService.actionOnFormSubmit();
        }, 1000);
        window.scroll(0, 0);
      } else {
        this.preventCanDeactivateCheck = true;
        if (this.environmentService.showTechnicalFormFirst) {
          this.route.navigate(['/personal-data']);
          this.stateShare.setFormState(3);
        } else {
          this.route.navigate(['/documents-submission']);
          this.stateShare.setFormState(4);
        }
      }
    } else {
      this.saveInstallationData();
    }
  }
}
